import {isEmpty} from 'lodash';
import isNil from 'lodash/isNil';
import {useEffect, useRef, useState} from 'react';
import usePost from '../../../../../../shared/hooks/use-post.hook';
import {CbsFieldGroup} from '../../../../../../shared/model/custom-field-type.model';
import {CustomerType} from '../../../../../../shared/model/customer-type.model';
import {FieldType} from '../../../../../../shared/model/field.model';
import {ExternalCustomFieldConfig} from '../../../../../shared/external-custom-field/ExternalCustomFieldSelection';

interface ExternalCustomFieldConfigState {
  areCustomConfigLoaded: boolean;
  externalCustomFieldConfigs?: ExternalCustomFieldConfig[];
}

export interface ExternalCustomFieldConfigSearchRequest {
  ids?: number[];
  customFieldGroups?: CbsFieldGroup[];
  customerTypes?: CustomerType[];
  availableInBranchIds?: number[];
  fieldType?: FieldType;
}

export default function useExternalCustomFieldConfig(searchRequest: ExternalCustomFieldConfigSearchRequest): ExternalCustomFieldConfigState {
  const searchExternalCustomFields = usePost<ExternalCustomFieldConfig[], ExternalCustomFieldConfigSearchRequest>('/configurations/external/custom-field/search');
  const [configs, setConfigs] = useState<ExternalCustomFieldConfig[]>();
  const previousRequest = useRef<string>();

  const requestString = JSON.stringify(searchRequest);
  const emptyRequest = !searchRequest || (isEmpty(searchRequest.ids) &&
    isEmpty(searchRequest.customFieldGroups) &&
    isEmpty(searchRequest.customerTypes) &&
    isEmpty(searchRequest.availableInBranchIds) &&
    !searchRequest.fieldType);

  useEffect(() => {
    const fetchConfigs = async () => {
      if (emptyRequest || previousRequest.current === requestString) {
        return;
      }

      const currentRequest = requestString;
      previousRequest.current = currentRequest;

      setConfigs(undefined);

      try {
        const result = await searchExternalCustomFields(searchRequest);
        if (previousRequest.current === currentRequest) {
          setConfigs(result);
        }
      } catch (error) {
        if (previousRequest.current === currentRequest) {
          previousRequest.current = undefined;
        }
        console.error('Failed to fetch external custom field configs:', error);
      }
    };

    fetchConfigs();
  }, [emptyRequest, requestString, searchExternalCustomFields]);

  const areCustomConfigLoaded = !isNil(configs) || emptyRequest;

  return {
    areCustomConfigLoaded,
    externalCustomFieldConfigs: emptyRequest ? [] : configs
  };
}